/**
 * The country past the old map. Grown once from a seed, the same on the client
 * and the server. Hand-built places stay exactly as they were; this only paints
 * where the old map left plain grass, plus everything east and south of it.
 */
import type { EnemyKind } from './types.ts';
import {
  DRAWN_H,
  MAP_H,
  MAP_W,
  OLD_H,
  OLD_W,
  T_ASH,
  T_COPPER,
  T_CRUST,
  T_DIRT,
  T_FLOOR,
  T_GLASS,
  T_GLOW,
  T_GRASS,
  T_GREASE,
  T_MARROW,
  T_MUD,
  T_PROP,
  T_ROCK,
  T_SALT,
  T_SCRUB,
  T_SHARD,
  T_STATIC,
  T_TOXIC,
  T_TRACK,
  T_TREE,
  T_VENT,
  T_WALL,
  T_WATER,
  hash2,
  noise,
  rng32,
  solid,
} from './tiles.ts';

export const WORLD_SEED = 0x5ad711e;

export type Biome = 'country' | 'scrub' | 'brights' | 'kettle' | 'sink' | 'dark' | 'town';
export type Mood = 'glass' | 'salt' | 'grease' | 'court' | 'marrow' | 'leak' | 'kettle' | 'sink' | 'dark' | 'brights';

export type Region = {
  id: string;
  name: string;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  biome: Biome;
  /** Fixed danger. Otherwise it rises with distance from the diner, inside [lo, hi]. */
  tier?: number;
  lo?: number;
  hi?: number;
  mood?: Mood;
  /** Nothing hostile spawns here from the seed. */
  calm?: boolean;
};

/** First match wins. Small hand-built places first, big wild bands last. */
export const REGIONS: Region[] = [
  { id: 'margin', name: 'The Margin', x0: 0, y0: 300, x1: 140, y1: 360, biome: 'country', tier: 8, mood: 'glass', calm: true },
  { id: 'terminus', name: 'The Terminus', x0: 140, y0: 300, x1: 560, y1: 360, biome: 'dark', tier: 10, mood: 'brights', calm: true },
  { id: 'undertrack', name: 'The Undertrack', x0: 2, y0: 260, x1: 60, y1: 300, biome: 'dark', tier: 6, mood: 'dark', calm: true },
  { id: 'lamps', name: 'The lamp rows', x0: 0, y0: 0, x1: 16, y1: 13, biome: 'country', tier: 1, mood: 'glass', calm: true },
  { id: 'hum', name: 'The bowl', x0: 16, y0: 0, x1: 36, y1: 13, biome: 'country', tier: 1, calm: true },
  { id: 'radville', name: 'Radville', x0: 0, y0: 13, x1: 62, y1: 44, biome: 'country', tier: 1, calm: true },
  { id: 'cut', name: 'The grease cut', x0: 4, y0: 44, x1: 32, y1: 66, biome: 'country', tier: 1, mood: 'grease', calm: true },
  { id: 'pan', name: 'The white pan', x0: 58, y0: 20, x1: 102, y1: 44, biome: 'country', tier: 2, mood: 'salt', calm: true },
  { id: 'mile', name: 'The long mile', x0: 100, y0: 24, x1: 184, y1: 38, biome: 'country', tier: 2, calm: true },
  { id: 'slow', name: 'The slow south', x0: 8, y0: 66, x1: 26, y1: 112, biome: 'country', tier: 2, calm: true },
  { id: 'court', name: 'The antenna court', x0: 184, y0: 18, x1: 214, y1: 46, biome: 'country', tier: 3, mood: 'court', calm: true },
  { id: 'marrow', name: 'The marrow yard', x0: 2, y0: 110, x1: 40, y1: 132, biome: 'country', tier: 3, mood: 'marrow', calm: true },
  { id: 'haltroad', name: 'The road to Halt', x0: 214, y0: 24, x1: 254, y1: 38, biome: 'country', tier: 4, calm: true },
  { id: 'halt', name: 'Halt', x0: 254, y0: 20, x1: 274, y1: 40, biome: 'town', tier: 4, calm: true },
  { id: 'leak', name: 'The Leak', x0: 274, y0: 16, x1: 374, y1: 52, biome: 'country', tier: 5, mood: 'leak', calm: true },
  { id: 'sump', name: 'Sump', x0: 258, y0: 192, x1: 300, y1: 224, biome: 'town', tier: 5, calm: true },
  { id: 'kettlelair', name: 'The Kettle', x0: 66, y0: 222, x1: 104, y1: 254, biome: 'kettle', tier: 6, mood: 'kettle', calm: true },
  { id: 'station', name: 'The dark station', x0: 468, y0: 226, x1: 536, y1: 274, biome: 'dark', tier: 8, mood: 'dark', calm: true },
  { id: 'orchard', name: 'The glass orchard', x0: 440, y0: 34, x1: 492, y1: 70, biome: 'brights', tier: 7, mood: 'brights', calm: true },
  { id: 'open', name: 'The open country', x0: 36, y0: 0, x1: 214, y1: 24, biome: 'country', lo: 2, hi: 3 },
  { id: 'back', name: 'The back country', x0: 26, y0: 38, x1: 190, y1: 112, biome: 'country', lo: 2, hi: 3 },
  { id: 'lot', name: 'The back lot', x0: 26, y0: 112, x1: 200, y1: 132, biome: 'country', lo: 3, hi: 3 },
  { id: 'highscrub', name: 'The high scrub', x0: 214, y0: 0, x1: 378, y1: 16, biome: 'scrub', lo: 4, hi: 5 },
  { id: 'scrub', name: 'The scrub', x0: 190, y0: 38, x1: 378, y1: 132, biome: 'scrub', lo: 4, hi: 5 },
  { id: 'brights', name: 'The brights', x0: 374, y0: 0, x1: 560, y1: 140, biome: 'brights', lo: 6, hi: 7, mood: 'brights' },
  { id: 'kettle', name: 'Kettle flats', x0: 0, y0: 132, x1: 190, y1: 300, biome: 'kettle', lo: 4, hi: 5, mood: 'kettle' },
  { id: 'sink', name: 'The sink', x0: 190, y0: 132, x1: 380, y1: 300, biome: 'sink', lo: 5, hi: 6, mood: 'sink' },
  { id: 'dark', name: 'The dark reach', x0: 380, y0: 140, x1: 560, y1: 300, biome: 'dark', lo: 7, hi: 8, mood: 'dark' },
];

const FALLBACK: Region = { id: 'country', name: 'The country', x0: 0, y0: 0, x1: MAP_W, y1: MAP_H, biome: 'country', lo: 1, hi: 8 };
const DINER_TX = 18;
const DINER_TY = 26;

export function regionAtTile(tx: number, ty: number): Region {
  for (const region of REGIONS) {
    if (tx >= region.x0 && tx < region.x1 && ty >= region.y0 && ty < region.y1) return region;
  }
  return FALLBACK;
}

export function tierAtTile(tx: number, ty: number): number {
  return tierIn(regionAtTile(tx, ty), tx, ty);
}

/**
 * What the seed sees. It grew the country before there was anything past the
 * drawn rows, and its borders wander a few tiles over the edge, so it has to keep
 * seeing plain country there or the whole south would grow differently.
 */
function grownRegion(tx: number, ty: number): Region {
  return ty >= DRAWN_H ? FALLBACK : regionAtTile(tx, ty);
}

function grownTier(tx: number, ty: number): number {
  return tierIn(grownRegion(tx, ty), tx, ty);
}

function tierIn(region: Region, tx: number, ty: number): number {
  if (region.tier) return region.tier;
  const d = Math.hypot(tx - DINER_TX, ty - DINER_TY);
  const t = Math.round(1 + d / 72);
  return Math.max(region.lo ?? 1, Math.min(region.hi ?? 8, t));
}

// ---------------------------------------------------------------------------
// What the seed produces, besides ground.

export type WildSpawn = {
  kind: EnemyKind;
  tx: number;
  ty: number;
  roam: number;
  tier: number;
  champ?: boolean;
  boss?: boolean;
  name?: string;
};

export type Cache = {
  id: string;
  name: string;
  x: number;
  y: number;
  tier: number;
  face: 'crate' | 'barrel' | 'wreck' | 'chest';
  /** Pushes the rarity roll. */
  boost: number;
  /** Refills per person after this many seconds. */
  back: number;
};

export type Poi = { id: string; name: string; kind: 'camp' | 'wreck' | 'den' | 'stash' | 'lair' | 'town'; tx: number; ty: number; tier: number };
export type WildNode = { id: string; stuff: 'scrap' | 'wire' | 'rag' | 'bulb' | 'grit' | 'drip' | 'slag'; n: number; x: number; y: number };
export type Prop = { kind: 'tent' | 'deadfire' | 'bones' | 'sign' | 'post' | 'board' | 'lamppost' | 'pylon' | 'kettle' | 'pipe' | 'stone' | 'gear' | 'bench' | 'stair' | 'coach' | 'clock' | 'buffer'; x: number; y: number; text?: string };
/** A stone that hums. Touch it and you hit harder and walk faster, for a while. */
export type Stone = { id: string; x: number; y: number };

export type Gen = {
  spawns: WildSpawn[];
  caches: Cache[];
  pois: Poi[];
  nodes: WildNode[];
  props: Prop[];
  stones: Stone[];
  /** Spawns down in the Undertrack. Not walkable from the diner, only by stair. */
  under: WildSpawn[];
  /** Spawns past the edge of the map: the Margin and the Terminus. See edge.ts. */
  edge: WildSpawn[];
};

export const GEN: Gen = { spawns: [], caches: [], pois: [], nodes: [], props: [], stones: [], under: [], edge: [] };

const TILE = 16;
const px = (t: number) => t * TILE + 8;

/** Monster tables. [kind, first tier it shows up at]. */
const TABLES: Record<Exclude<Biome, 'town'>, [EnemyKind, number][]> = {
  country: [
    ['roach', 1],
    ['fastroach', 1],
    ['drifter', 2],
    ['sleeper', 2],
    ['toad', 2],
    ['hound', 3],
  ],
  scrub: [
    ['hound', 3],
    ['tick', 3],
    ['drifter', 3],
    ['fastroach', 3],
    ['crab', 4],
    ['walker', 4],
  ],
  brights: [
    ['leaker', 5],
    ['filament', 5],
    ['moth', 6],
    ['walker', 6],
  ],
  kettle: [
    ['panback', 3],
    ['skip', 3],
    ['toad', 3],
    ['hound', 4],
    ['crab', 5],
  ],
  sink: [
    ['slick', 4],
    ['newt', 4],
    ['slug', 5],
    ['hound', 5],
    ['leaker', 6],
  ],
  dark: [
    ['mite', 6],
    ['nail', 6],
    ['clerk', 7],
    ['walker', 7],
  ],
};

const GROUND: Record<Biome, number> = {
  country: T_GRASS,
  scrub: T_DIRT,
  brights: T_GLASS,
  kettle: T_ASH,
  sink: T_MUD,
  dark: T_STATIC,
  town: T_FLOOR,
};

const OBSTACLE: Record<Biome, number> = {
  country: T_ROCK,
  scrub: T_ROCK,
  brights: T_SHARD,
  kettle: T_CRUST,
  sink: T_ROCK,
  dark: T_WALL,
  town: T_WALL,
};

const NODE_STUFF: Record<Biome, WildNode['stuff'][]> = {
  country: ['scrap', 'rag', 'bulb'],
  scrub: ['scrap', 'wire', 'rag'],
  brights: ['slag', 'bulb'],
  kettle: ['grit', 'scrap'],
  sink: ['drip', 'rag'],
  dark: ['wire', 'slag'],
  town: ['scrap'],
};

const CAMP_NAMES = ["Somebody's camp", 'A folded tent', 'The quiet porch', "Last week's camp", 'A burned kitchen', 'A lean-to', 'The card table'];
const DEN_NAMES = ['A den', 'The nest', "Something's bed", 'A warm hole'];

/** Hand-built doors and roads that join the old map to the new country. */
export function growWorld(m: number[][], spawnTx: number, spawnTy: number, keep: [number, number][] = []): void {
  GEN.spawns.length = 0;
  GEN.caches.length = 0;
  GEN.pois.length = 0;
  GEN.nodes.length = 0;
  GEN.props.length = 0;
  GEN.stones.length = 0;
  GEN.under.length = 0;
  GEN.edge.length = 0;

  const rand = rng32(WORLD_SEED);
  // Only the drawn country grows. The rows past it belong to the story (see edge.ts).
  const H = DRAWN_H;
  const W = MAP_W;
  const inb = (x: number, y: number) => x >= 0 && y >= 0 && x < W && y < H;
  const set = (x: number, y: number, t: number) => {
    if (inb(x, y)) m[y][x] = t;
  };
  const fill = (x: number, y: number, w: number, h: number, t: number) => {
    for (let ty = y; ty < y + h; ty++) for (let tx = x; tx < x + w; tx++) set(tx, ty, t);
  };
  const hollow = (x: number, y: number, w: number, h: number, wall: number, floor: number) => {
    fill(x, y, w, h, wall);
    fill(x + 1, y + 1, w - 2, h - 2, floor);
  };

  // Protected tiles are never touched by the wild painter.
  const guard = new Uint8Array(W * H);
  const protect = (x: number, y: number, w: number, h: number) => {
    for (let ty = y; ty < y + h; ty++) for (let tx = x; tx < x + w; tx++) if (inb(tx, ty)) guard[ty * W + tx] = 1;
  };
  // Everything the old map painted, and a margin around it.
  for (let ty = 0; ty < OLD_H; ty++) {
    for (let tx = 0; tx < OLD_W; tx++) {
      if (m[ty][tx] !== T_GRASS) protect(tx - 3, ty - 3, 7, 7);
    }
  }
  // The old town proper stays exactly as drawn, and so does every hand-placed spot.
  protect(0, 0, 62, 50);
  for (const [kx, ky] of keep) protect(kx - 2, ky - 2, 5, 5);

  // ---- Ground and obstacles.
  for (let ty = 0; ty < H; ty++) {
    for (let tx = 0; tx < W; tx++) {
      if (guard[ty * W + tx]) continue;
      const old = tx < OLD_W && ty < OLD_H;
      if (old && m[ty][tx] !== T_GRASS) continue;
      // Borders wander a little so the bands do not meet on a ruler line.
      const jx = Math.round((noise(tx, ty, 17, 51) - 0.5) * 22);
      const jy = Math.round((noise(tx, ty, 17, 53) - 0.5) * 22);
      let region = grownRegion(tx + jx, ty + jy);
      if (region.calm || region.biome === 'town') region = grownRegion(tx, ty);
      const biome = region.biome === 'town' ? 'country' : region.biome;
      const n1 = noise(tx, ty, 7, 11);
      const n2 = noise(tx, ty, 13, 23);
      const n3 = noise(tx, ty, 9, 37);
      const h = hash2(tx, ty, 5);
      let t = GROUND[biome];
      if (biome === 'country') {
        if (n2 > 0.66) t = T_DIRT;
        if (n3 > 0.86) t = T_WATER;
        else if (h < 0.018) t = T_TREE;
      } else if (biome === 'scrub') {
        if (n2 < 0.36) t = T_GRASS;
        if (h < 0.05) t = T_SCRUB;
        if (h > 0.985) t = T_TREE;
      } else if (biome === 'brights') {
        if (n2 > 0.62) t = T_GLOW;
        if (n3 > 0.84) t = T_TOXIC;
      } else if (biome === 'kettle') {
        if (n2 > 0.72) t = T_SALT;
        if (h < 0.012) t = T_VENT;
      } else if (biome === 'sink') {
        if (n2 > 0.6) t = T_GREASE;
        else if (n2 < 0.3) t = T_MARROW;
        if (n3 > 0.8) t = T_WATER;
      } else if (biome === 'dark') {
        if (n2 > 0.55) t = T_FLOOR;
        if (h < 0.01) t = T_COPPER;
      }
      if (n1 > 0.8 && t !== T_WATER) t = OBSTACLE[biome];
      m[ty][tx] = t;
    }
  }

  // ---- Roads. Straight, plain, and protected.
  const road = (points: [number, number][], width = 3) => {
    for (let i = 0; i < points.length - 1; i++) {
      const [ax, ay] = points[i];
      const [bx, by] = points[i + 1];
      const steps = Math.max(Math.abs(bx - ax), Math.abs(by - ay));
      for (let s = 0; s <= steps; s++) {
        const x = Math.round(ax + ((bx - ax) * s) / steps);
        const y = Math.round(ay + ((by - ay) * s) / steps);
        for (let dy = 0; dy < width; dy++) {
          for (let dx = 0; dx < width; dx++) {
            const tx = x + dx - 1;
            const ty = y + dy - 1;
            if (!inb(tx, ty)) continue;
            if (solid(m[ty][tx]) && guard[ty * W + tx] && ty < OLD_H && tx < OLD_W) continue;
            m[ty][tx] = T_TRACK;
          }
        }
        protect(x - 2, y - 2, width + 3, width + 3);
      }
    }
  };

  // Doors in the old walls.
  set(371, 30, T_TRACK);
  set(371, 31, T_TRACK);
  set(340, 49, T_GLOW);
  set(341, 49, T_GLOW);
  set(263, 37, T_DIRT);
  set(264, 37, T_DIRT);
  set(20, 129, T_MARROW);
  set(21, 129, T_MARROW);

  // Halt south to Sump.
  road([
    [263, 38],
    [263, 150],
    [279, 166],
    [279, 192],
  ]);
  // The Leak east, into the brights.
  road([
    [372, 30],
    [420, 30],
    [440, 50],
  ]);
  // The Leak south door into the scrub.
  road([
    [340, 50],
    [340, 80],
    [300, 110],
  ]);
  // Marrow yard south to the flats and the Kettle.
  road([
    [20, 130],
    [20, 170],
    [60, 200],
    [84, 222],
  ]);
  // Sump west across the flats, toward the yard road.
  road([
    [258, 208],
    [200, 208],
    [120, 190],
    [60, 200],
  ]);
  // Sump east to the dark station.
  road([
    [300, 208],
    [380, 212],
    [440, 240],
    [468, 248],
  ]);
  // Brights south into the dark reach.
  road([
    [440, 50],
    [470, 110],
    [470, 160],
    [500, 226],
  ]);
  // The back lot's east end, down into the scrub and on to Sump.
  road([
    [196, 121],
    [230, 150],
    [263, 150],
  ]);

  // ---- Authored places in the new country.
  // Sump: a walled second-and-a-half sitting on poured floor.
  protect(256, 190, 46, 36);
  hollow(262, 196, 34, 24, T_WALL, T_FLOOR);
  for (const [gx, gy] of [
    [278, 196],
    [279, 196],
    [280, 196],
    [278, 219],
    [279, 219],
    [280, 219],
    [262, 207],
    [262, 208],
    [262, 209],
    [295, 207],
    [295, 208],
    [295, 209],
  ] as const)
    set(gx, gy, T_FLOOR);
  fill(266, 200, 3, 2, T_WALL);
  fill(289, 200, 3, 2, T_WALL);
  GEN.props.push({ kind: 'sign', x: 276 * TILE, y: 193 * TILE, text: 'SUMP' });
  GEN.props.push({ kind: 'deadfire', x: px(279), y: px(208) });
  GEN.props.push({ kind: 'lamppost', x: px(270), y: px(212) });
  GEN.props.push({ kind: 'lamppost', x: px(288), y: px(212) });
  GEN.pois.push({ id: 'sump', name: 'Sump', kind: 'town', tx: 279, ty: 208, tier: 5 });

  // The Kettle: a cracked crater with a boiling middle.
  protect(64, 220, 42, 36);
  for (let ty = 222; ty < 254; ty++) {
    for (let tx = 66; tx < 104; tx++) {
      const d = Math.hypot((tx - 85) / 1.2, ty - 238);
      if (d < 13) set(tx, ty, T_ASH);
      else if (d < 15.5) set(tx, ty, T_CRUST);
    }
  }
  for (let ty = 222; ty < 236; ty++) for (const tx of [83, 84, 85]) set(tx, ty, T_ASH);
  for (const [vx, vy] of [
    [80, 236],
    [90, 240],
    [86, 244],
    [78, 242],
    [92, 234],
  ] as const)
    set(vx, vy, T_VENT);
  GEN.props.push({ kind: 'kettle', x: px(85), y: px(238) });
  GEN.pois.push({ id: 'kettlelair', name: 'The Kettle', kind: 'lair', tx: 85, ty: 238, tier: 6 });
  GEN.spawns.push({ kind: 'kettle', tx: 85, ty: 240, roam: 0, tier: 6, boss: true });
  for (const [sx, sy] of [
    [79, 238],
    [91, 238],
    [85, 232],
  ] as const)
    GEN.spawns.push({ kind: 'panback', tx: sx, ty: sy, roam: 2, tier: 6 });
  GEN.caches.push({ id: 'kettlehoard', name: 'Kettle hoard', x: px(85), y: px(246), tier: 6, face: 'chest', boost: 3, back: 1500 });

  // The dark station: a long building with rooms, and something that still runs it.
  protect(466, 224, 72, 52);
  hollow(468, 226, 68, 48, T_WALL, T_FLOOR);
  for (const gy of [248, 249, 250]) set(468, gy, T_FLOOR);
  fill(490, 227, 1, 18, T_WALL);
  fill(490, 256, 1, 17, T_WALL);
  fill(512, 227, 1, 14, T_WALL);
  fill(512, 252, 1, 21, T_WALL);
  fill(469, 238, 10, 1, T_WALL);
  fill(482, 238, 8, 1, T_WALL);
  fill(469, 262, 8, 1, T_WALL);
  fill(480, 262, 10, 1, T_WALL);
  fill(513, 246, 10, 1, T_WALL);
  fill(526, 246, 9, 1, T_WALL);
  GEN.props.push({ kind: 'sign', x: 452 * TILE, y: 244 * TILE, text: 'DARK STATION' });
  GEN.pois.push({ id: 'station', name: 'The dark station', kind: 'lair', tx: 524, ty: 262, tier: 8 });
  GEN.spawns.push({ kind: 'master', tx: 524, ty: 262, roam: 0, tier: 8, boss: true });
  for (const [sx, sy, kind] of [
    [478, 232, 'mite'],
    [484, 232, 'mite'],
    [476, 250, 'clerk'],
    [500, 234, 'nail'],
    [504, 244, 'clerk'],
    [498, 262, 'mite'],
    [506, 266, 'nail'],
    [520, 234, 'clerk'],
    [528, 238, 'mite'],
    [518, 256, 'walker'],
  ] as const)
    GEN.spawns.push({ kind, tx: sx, ty: sy, roam: 3, tier: 8 });
  GEN.caches.push({ id: 'stationsafe', name: 'Station safe', x: px(530), y: px(268), tier: 8, face: 'chest', boost: 4, back: 1500 });
  GEN.caches.push({ id: 'stationdesk', name: 'Clerk desk', x: px(500), y: px(230), tier: 8, face: 'crate', boost: 2, back: 1200 });

  // The glass orchard: rows of glass trees in the brights, and a thing that tends them.
  protect(438, 32, 56, 40);
  fill(440, 34, 52, 36, T_GLASS);
  fill(446, 40, 40, 24, T_GLOW);
  for (let ty = 37; ty < 68; ty += 5) {
    for (let tx = 443; tx < 490; tx += 5) {
      if (hash2(tx, ty, 7) < 0.8) set(tx, ty, T_SHARD);
    }
  }
  fill(460, 46, 10, 10, T_GLASS);
  GEN.pois.push({ id: 'orchard', name: 'The glass orchard', kind: 'lair', tx: 465, ty: 51, tier: 7 });
  GEN.spawns.push({ kind: 'moth', tx: 465, ty: 51, roam: 4, tier: 7, champ: true, name: 'The Gardener' });
  for (const [sx, sy] of [
    [450, 40],
    [480, 40],
    [450, 62],
    [480, 62],
    [470, 55],
  ] as const)
    GEN.spawns.push({ kind: 'moth', tx: sx, ty: sy, roam: 4, tier: 7 });
  GEN.caches.push({ id: 'orchardroot', name: 'Root cellar', x: px(465), y: px(51), tier: 7, face: 'chest', boost: 3, back: 1500 });

  // Signs at the edges of the new country.
  GEN.props.push({ kind: 'sign', x: 376 * TILE, y: 27 * TILE, text: 'THE BRIGHTS' });
  GEN.props.push({ kind: 'sign', x: 23 * TILE, y: 134 * TILE, text: 'KETTLE FLATS' });
  GEN.props.push({ kind: 'sign', x: 266 * TILE, y: 134 * TILE, text: 'THE SINK' });
  GEN.props.push({ kind: 'sign', x: 343 * TILE, y: 54 * TILE, text: 'THE SCRUB' });
  GEN.props.push({ kind: 'sign', x: 442 * TILE, y: 150 * TILE, text: 'DARK REACH' });
  GEN.props.push({ kind: 'pylon', x: px(263), y: px(90) });
  GEN.props.push({ kind: 'pylon', x: px(420), y: px(34) });
  GEN.props.push({ kind: 'pylon', x: px(150), y: px(196) });
  GEN.props.push({ kind: 'pylon', x: px(380), y: px(215) });

  // ---- Points of interest on a jittered grid.
  const CELL = 26;
  let poiCount = 0;
  const clearArea = (x: number, y: number, w: number, h: number) => {
    for (let ty = y; ty < y + h; ty++) for (let tx = x; tx < x + w; tx++) if (!inb(tx, ty) || guard[ty * W + tx]) return false;
    return true;
  };
  for (let cy = 0; cy < Math.ceil(H / CELL); cy++) {
    for (let cx = 0; cx < Math.ceil(W / CELL); cx++) {
      const ox = cx * CELL + 4 + Math.floor(rand() * (CELL - 16));
      const oy = cy * CELL + 4 + Math.floor(rand() * (CELL - 14));
      const pick = rand();
      if (pick < 0.3) continue;
      if (!clearArea(ox - 2, oy - 2, 15, 13)) continue;
      const region = grownRegion(ox + 5, oy + 4);
      if (region.calm || region.biome === 'town') continue;
      const tier = grownTier(ox + 5, oy + 4);
      const ground = GROUND[region.biome];
      const wall = region.biome === 'dark' ? T_WALL : region.biome === 'brights' ? T_SHARD : region.biome === 'kettle' ? T_CRUST : T_ROCK;
      const id = `p${poiCount++}`;
      fill(ox - 1, oy - 1, 13, 11, ground);
      if (pick < 0.55) {
        // A camp. Walls with two gaps, a crate inside, people who did not leave.
        hollow(ox, oy, 11, 9, wall, region.biome === 'country' || region.biome === 'scrub' ? T_DIRT : ground);
        set(ox + 5, oy, ground);
        set(ox + 6, oy, ground);
        set(ox + 5, oy + 8, ground);
        set(ox + 6, oy + 8, ground);
        set(ox, oy + 4, ground);
        set(ox + 10, oy + 4, ground);
        const name = CAMP_NAMES[Math.floor(rand() * CAMP_NAMES.length)];
        GEN.pois.push({ id, name, kind: 'camp', tx: ox + 5, ty: oy + 4, tier });
        GEN.caches.push({ id: `${id}c`, name: 'Camp crate', x: px(ox + 3), y: px(oy + 2), tier, face: 'crate', boost: 1.5, back: 900 });
        GEN.props.push({ kind: 'deadfire', x: px(ox + 6), y: px(oy + 5) });
        GEN.props.push({ kind: 'tent', x: px(ox + 8), y: px(oy + 3) });
        packAt(ox + 5, oy + 4, region.biome, tier, 3 + Math.floor(rand() * 2), rand() < 0.2, 2);
      } else if (pick < 0.72) {
        // A dead car with a trunk.
        fill(ox + 3, oy + 3, 3, 2, T_PROP);
        GEN.pois.push({ id, name: 'A dead car', kind: 'wreck', tx: ox + 4, ty: oy + 5, tier });
        GEN.caches.push({ id: `${id}c`, name: 'Dead trunk', x: px(ox + 4), y: px(oy + 6), tier, face: 'wreck', boost: 1.2, back: 900 });
        packAt(ox + 8, oy + 6, region.biome, tier, 2, false, 4);
      } else if (pick < 0.88) {
        // A den. Boulders, bones, and something loud.
        for (const [dx, dy] of [
          [1, 1],
          [9, 1],
          [1, 7],
          [9, 7],
          [5, 0],
          [0, 4],
        ] as const)
          set(ox + dx, oy + dy, wall);
        GEN.props.push({ kind: 'bones', x: px(ox + 4), y: px(oy + 4) });
        GEN.props.push({ kind: 'bones', x: px(ox + 7), y: px(oy + 5) });
        const name = DEN_NAMES[Math.floor(rand() * DEN_NAMES.length)];
        GEN.pois.push({ id, name, kind: 'den', tx: ox + 5, ty: oy + 4, tier });
        GEN.caches.push({ id: `${id}c`, name: 'Picked bones', x: px(ox + 5), y: px(oy + 6), tier, face: 'barrel', boost: 2, back: 900 });
        packAt(ox + 5, oy + 4, region.biome, tier, 3 + Math.floor(rand() * 2), true, 2);
      } else {
        // A buried tin. Nobody guards it. Somebody meant to come back.
        set(ox + 3, oy + 3, wall);
        set(ox + 7, oy + 5, wall);
        GEN.pois.push({ id, name: 'A buried tin', kind: 'stash', tx: ox + 5, ty: oy + 4, tier });
        GEN.caches.push({ id: `${id}c`, name: 'Buried tin', x: px(ox + 5), y: px(oy + 4), tier, face: 'barrel', boost: 1.6, back: 1200 });
      }
      protect(ox - 1, oy - 1, 13, 11);
    }
  }

  function packAt(tx: number, ty: number, biome: Biome, tier: number, size: number, champ: boolean, roam: number): void {
    if (biome === 'town') return;
    const table = TABLES[biome].filter(([, from]) => from <= tier);
    if (!table.length) return;
    const [kind] = table[Math.floor(rand() * table.length)];
    for (let i = 0; i < size; i++) {
      GEN.spawns.push({
        kind,
        tx: tx + Math.round((rand() - 0.5) * 4),
        ty: ty + Math.round((rand() - 0.5) * 4),
        roam,
        tier,
        champ: champ && i === 0,
      });
    }
  }

  // ---- Reach. Everything that matters has to be walkable from the diner.
  const reach = new Uint8Array(W * H);
  const flood = () => {
    reach.fill(0);
    const queue = new Int32Array(W * H);
    let head = 0;
    let tail = 0;
    const start = spawnTy * W + spawnTx;
    reach[start] = 1;
    queue[tail++] = start;
    while (head < tail) {
      const cur = queue[head++];
      const x = cur % W;
      const y = (cur - x) / W;
      const next = [cur - 1, cur + 1, cur - W, cur + W];
      const ok = [x > 0, x < W - 1, y > 0, y < H - 1];
      for (let i = 0; i < 4; i++) {
        if (!ok[i]) continue;
        const n = next[i];
        if (reach[n]) continue;
        const nx = n % W;
        const ny = (n - nx) / W;
        if (solid(m[ny][nx])) continue;
        reach[n] = 1;
        queue[tail++] = n;
      }
    }
  };
  const carveTo = (tx: number, ty: number) => {
    // Walk outward until a reached tile turns up, then cut an L back to it.
    for (let r = 1; r < 60; r++) {
      for (let dy = -r; dy <= r; dy++) {
        for (let dx = -r; dx <= r; dx++) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
          const x = tx + dx;
          const y = ty + dy;
          if (!inb(x, y) || !reach[y * W + x]) continue;
          const ground = GROUND[grownRegion(tx, ty).biome];
          const cut = (cx: number, cy: number) => {
            if (!inb(cx, cy)) return;
            if (solid(m[cy][cx]) && !(cy < OLD_H && cx < OLD_W && guard[cy * W + cx])) m[cy][cx] = ground;
          };
          for (let s = 0; s <= Math.abs(dx); s++) cut(tx + Math.sign(dx) * s, ty);
          for (let s = 0; s <= Math.abs(dy); s++) cut(x, ty + Math.sign(dy) * s);
          return;
        }
      }
    }
  };
  const anchors = (): [number, number][] => [
    ...GEN.pois.map((poi) => [poi.tx, poi.ty] as [number, number]),
    ...GEN.caches.map((cache) => [Math.floor(cache.x / TILE), Math.floor(cache.y / TILE)] as [number, number]),
    ...GEN.spawns.map((spawn) => [spawn.tx, spawn.ty] as [number, number]),
  ];
  // Anything standing on a solid tile gets its tile cleared first.
  for (const [ax, ay] of anchors()) if (inb(ax, ay) && solid(m[ay][ax])) m[ay][ax] = GROUND[grownRegion(ax, ay).biome];
  flood();
  for (let pass = 0; pass < 6; pass++) {
    let missing = 0;
    for (const [ax, ay] of anchors()) {
      if (!inb(ax, ay) || reach[ay * W + ax]) continue;
      missing++;
      carveTo(ax, ay);
    }
    if (!missing) break;
    flood();
  }
  // Pockets nobody can reach get filled in, so nothing spawns inside a rock.
  for (let ty = 0; ty < H; ty++) {
    for (let tx = 0; tx < W; tx++) {
      if (reach[ty * W + tx] || solid(m[ty][tx])) continue;
      if (tx < OLD_W && ty < OLD_H) continue;
      m[ty][tx] = OBSTACLE[grownRegion(tx, ty).biome];
    }
  }
  // Drop anything that still could not be reached. Better missing than stuck.
  const okTile = (tx: number, ty: number) => inb(tx, ty) && reach[ty * W + tx] === 1 && !solid(m[ty][tx]);
  GEN.spawns.splice(0, GEN.spawns.length, ...GEN.spawns.filter((spawn) => okTile(spawn.tx, spawn.ty)));
  GEN.caches.splice(0, GEN.caches.length, ...GEN.caches.filter((cache) => okTile(Math.floor(cache.x / TILE), Math.floor(cache.y / TILE))));

  // ---- Wanderers between the places. Sparse, on purpose.
  const WCELL = 17;
  for (let cy = 0; cy < Math.ceil(H / WCELL); cy++) {
    for (let cx = 0; cx < Math.ceil(W / WCELL); cx++) {
      const tx = cx * WCELL + Math.floor(rand() * WCELL);
      const ty = cy * WCELL + Math.floor(rand() * WCELL);
      const roll = rand();
      if (!okTile(tx, ty) || guard[ty * W + tx]) continue;
      const region = grownRegion(tx, ty);
      if (region.calm || region.biome === 'town') continue;
      if (roll > 0.5) continue;
      const tier = grownTier(tx, ty);
      packAt(tx, ty, region.biome, tier, 1 + Math.floor(rand() * 3), rand() < 0.07, 6);
    }
  }
  GEN.spawns.splice(0, GEN.spawns.length, ...GEN.spawns.filter((spawn) => okTile(spawn.tx, spawn.ty)));

  // ---- Humming stones. Rare, out of the way, worth the detour.
  const SCELL = 48;
  for (let cy = 0; cy < Math.ceil(H / SCELL); cy++) {
    for (let cx = 0; cx < Math.ceil(W / SCELL); cx++) {
      const tx = cx * SCELL + 8 + Math.floor(rand() * (SCELL - 16));
      const ty = cy * SCELL + 8 + Math.floor(rand() * (SCELL - 16));
      if (rand() > 0.6 || !okTile(tx, ty) || guard[ty * W + tx]) continue;
      const region = grownRegion(tx, ty);
      if (region.calm || region.biome === 'town') continue;
      const id = `s${GEN.stones.length}`;
      GEN.stones.push({ id, x: px(tx), y: px(ty) });
      GEN.props.push({ kind: 'stone', x: px(tx), y: px(ty) });
    }
  }

  // ---- Piles to gather.
  let nodeCount = 0;
  const NCELL = 22;
  for (let cy = 0; cy < Math.ceil(H / NCELL); cy++) {
    for (let cx = 0; cx < Math.ceil(W / NCELL); cx++) {
      const tx = cx * NCELL + Math.floor(rand() * NCELL);
      const ty = cy * NCELL + Math.floor(rand() * NCELL);
      if (rand() > 0.55 || !okTile(tx, ty)) continue;
      if (tx < 62 && ty < 50) continue;
      const region = grownRegion(tx, ty);
      const list = NODE_STUFF[region.biome];
      const stuff = list[Math.floor(rand() * list.length)];
      GEN.nodes.push({ id: `n${nodeCount++}`, stuff, n: 1 + (rand() < 0.4 ? 1 : 0), x: px(tx), y: px(ty) });
    }
  }

  // ---- The Undertrack. Sealed in the southwest corner, reached only by the stair
  // under the Click Station. Stamped last, over whatever grew there.
  const UX0 = 2;
  const UY0 = 260;
  const UX1 = 60;
  const UY1 = 300;
  const inside = (tx: number, ty: number) => tx >= UX0 && tx < UX1 && ty >= UY0 && ty < UY1;
  const insidePx = (x: number, y: number) => inside(Math.floor(x / TILE), Math.floor(y / TILE));
  fill(4, 262, 54, 36, T_WALL);
  fill(6, 264, 12, 10, T_FLOOR); // the landing
  fill(18, 267, 14, 3, T_FLOOR);
  fill(32, 264, 12, 10, T_FLOOR); // the gear room
  fill(36, 274, 3, 8, T_FLOOR);
  fill(9, 274, 3, 2, T_FLOOR);
  fill(6, 276, 10, 10, T_FLOOR); // the clerks' room
  fill(16, 283, 4, 3, T_FLOOR);
  fill(20, 282, 26, 6, T_FLOOR); // the lower hall
  fill(46, 278, 10, 17, T_FLOOR); // the works
  fill(24, 289, 20, 6, T_FLOOR); // the spring room, behind the works
  fill(44, 291, 2, 2, T_FLOOR);
  for (const [cx, cy] of [
    [35, 267],
    [40, 267],
    [35, 271],
    [40, 271],
    [25, 284],
    [31, 285],
    [37, 284],
    [48, 282],
    [53, 282],
    [9, 279],
    [12, 282],
    [28, 290],
    [28, 293],
    [39, 290],
    [39, 293],
  ] as const)
    set(cx, cy, T_COPPER);
  const scrub = <T>(list: T[], at: (item: T) => [number, number]) => list.splice(0, list.length, ...list.filter((item) => !inside(...at(item))));
  scrub(GEN.spawns, (spawn) => [spawn.tx, spawn.ty]);
  scrub(GEN.caches, (cache) => [Math.floor(cache.x / TILE), Math.floor(cache.y / TILE)]);
  scrub(GEN.nodes, (node) => [Math.floor(node.x / TILE), Math.floor(node.y / TILE)]);
  scrub(GEN.stones, (stone) => [Math.floor(stone.x / TILE), Math.floor(stone.y / TILE)]);
  GEN.props.splice(0, GEN.props.length, ...GEN.props.filter((prop) => !insidePx(prop.x, prop.y)));
  GEN.pois.splice(0, GEN.pois.length, ...GEN.pois.filter((poi) => !inside(poi.tx, poi.ty)));
  for (const [gx, gy] of [
    [38, 265],
    [33, 272],
    [43, 269],
    [22, 283],
    [44, 286],
    [50, 279],
    [54, 293],
    [7, 277],
    [26, 291],
    [41, 292],
  ] as const)
    GEN.props.push({ kind: 'gear', x: px(gx), y: px(gy) });
  GEN.props.push({ kind: 'stair', x: px(7), y: px(265) });
  const under: [EnemyKind, number, number][] = [
    ['mite', 35, 266],
    ['mite', 40, 269],
    ['mite', 38, 272],
    ['nail', 42, 265],
    ['walker', 27, 268],
    ['clerk', 10, 280],
    ['clerk', 14, 283],
    ['mite', 8, 284],
    ['walker', 26, 283],
    ['nail', 34, 286],
    ['clerk', 41, 283],
    ['mite', 30, 283],
    ['mite', 22, 286],
    ['clerk', 48, 290],
    ['clerk', 54, 290],
  ];
  for (const [kind, tx, ty] of under) GEN.under.push({ kind, tx, ty, roam: 2, tier: 6 });
  GEN.under.push({ kind: 'foreman', tx: 51, ty: 286, roam: 0, tier: 6, boss: true });
  GEN.under.push({ kind: 'mainspring', tx: 33, ty: 291, roam: 0, tier: 8, boss: true });
  GEN.caches.push({ id: 'undercrate', name: 'Parts bin', x: px(7), y: px(285), tier: 6, face: 'chest', boost: 2.5, back: 1200 });
  GEN.caches.push({ id: 'undershelf', name: 'Tool shelf', x: px(43), y: px(272), tier: 6, face: 'crate', boost: 1.8, back: 900 });
}

